import {
  Avatar,
  Card,
  CardBody,
  Icon,
  LineBreak,
  Paragraph,
  Pill,
  PrimaryNavigation,
  PrimaryNavigationButton,
  Separator,
  Text,
} from "@george-labs.com/design-system"
import { OAuth2ClientData } from "@smartx/mikro-vt-client-api"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import {
  useNotifications,
  useProfiles,
  useSelectedProfile,
  useSelectedProfileId,
} from "../../app/hooks"
import AuthContext, { isFromGeorgeWeb } from "../../providers/AuthProvider"
import Loader from "../Loader/Loader"
import LogoutButton from "../LogoutButton/LogoutButton"
import ModalComponent from "../Modal/Modal"
import { ICON } from "@george-labs.com/george-assets/icons"

const Navbar = () => {
  const [unread, setUnread] = useState(0)
  const [logoutModalState, setLogoutModalState] = useState<boolean>(false)
  const [guard, setGuard] = useState(false)
  const [noMikro, setNoMikro] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const [showBackToGeorgeButton, setShowBackToGeorgeButton] = useState(false)
  const [backButtonRoute, setBackButtonRoute] = useState<string | undefined>(
    undefined,
  )

  const authContext = useContext(AuthContext)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const notifications = useNotifications()

  const selectedProfileId = useSelectedProfileId()
  const selectedProfile = useSelectedProfile()
  const profiles = useProfiles()

  const checkRoute = (route: string) => {
    const mikroCompanies = profiles.filter(
      (profile: OAuth2ClientData) => profile.mikroCompany,
    )?.length

    setMenuOpen(false)

    if (selectedProfileId && mikroCompanies > 0) {
      navigate(route)
    } else if (!selectedProfileId && mikroCompanies === 0) {
      setNoMikro(true)
    } else if (!selectedProfileId && mikroCompanies > 0) {
      setGuard(true)
    }
  }

  useEffect(() => {
    setShowBackToGeorgeButton(false)
    setBackButtonRoute(undefined)

    if (location.pathname === "/dashboard") {
      if (isFromGeorgeWeb()) {
        setShowBackToGeorgeButton(true)
      } else if (!!selectedProfile && profiles.length > 1) {
        setBackButtonRoute("./user-selection")
      }
    } else if (location.pathname !== "/user-selection") {
      setBackButtonRoute("./dashboard")
    }
  }, [location, profiles, selectedProfile])

  useEffect(() => {
    const unreadMessages = notifications.filter((i) => !i.read).length
    setUnread(unreadMessages)
  }, [notifications, unread])

  if (!authContext.isLoggedIn) {
    return <Loader></Loader>
  }

  const userInfo =
    selectedProfile?.name && selectedProfile?.taxNumber ? (
      <div style={{ textAlign: "center" }}>
        <Paragraph small>
          {selectedProfile.name}
          <LineBreak />({selectedProfile.taxNumber})
        </Paragraph>
      </div>
    ) : (
      <span> {selectedProfile?.name || ""}</span>
    )

  const doNavigate = async () => {
    if (showBackToGeorgeButton) {
      authContext.georgeExchange()
    } else if (backButtonRoute) {
      navigate(backButtonRoute)
    }
  }

  const backButtonContent = (
    <PrimaryNavigationButton key="left-button-1" onClick={doNavigate}>
      <Icon
        className="mt-4"
        assetIcon={ICON.ARROW_LEFT}
        size={Icon.SIZE.SIZE_24}
      />
      <div className="nav-dashboard" />
    </PrimaryNavigationButton>
  )

  return (
    <>
      <PrimaryNavigation
        sticky
        logo={userInfo}
        hideLogo={true}
        isOpen={menuOpen}
        onToggle={(isOpen) => setMenuOpen(!!isOpen)}
        className="sticky grid-eq-cols"
        menuButton={{
          xs: (
            <PrimaryNavigationButton icon={PrimaryNavigationButton.ICON.MENU}>
              <></>
            </PrimaryNavigationButton>
          ),
          lg: null,
        }}
        menuContent={
          <Card className="m-0" nested={false}>
            {isFromGeorgeWeb() && (
              <CardBody style={{ cursor: "pointer" }} onClick={doNavigate}>
                <Text>{t("menu.backToGeorge")}</Text>
                <Separator />
              </CardBody>
            )}
            {selectedProfile && profiles?.length > 1 && (
              <>
                <CardBody
                  style={{ cursor: "pointer" }}
                  onClick={() => checkRoute("/user-selection")}
                >
                  <Text>{t("menu.profileSelection")}</Text>
                </CardBody>
                <Separator />
              </>
            )}
            <CardBody
              style={{ cursor: "pointer" }}
              onClick={() => checkRoute("/dashboard")}
            >
              <Text>{t("menu.dashboard")}</Text>
            </CardBody>
            <Separator />
            <CardBody
              style={{ cursor: "pointer" }}
              onClick={() => checkRoute("/notifications")}
            >
              <div className="d-flex align-center">
                <Text className="d-inline">{t("menu.notifications")}</Text>
                {unread > 0 ? (
                  <Pill
                    style={{ height: "16px" }}
                    className="d-inline-flex ml-2 my-auto"
                    value={unread}
                    variant={Pill.VARIANT.UNREAD}
                  />
                ) : null}
              </div>
            </CardBody>
            <Separator />
            <CardBody
              style={{ cursor: "pointer" }}
              onClick={() => checkRoute("/profile")}
            >
              <Text>{t("menu.profile")}</Text>
            </CardBody>            
          </Card>
        }
        logoutButton={<LogoutButton />}
        right={[
          <PrimaryNavigationButton
            hiddenLabel={PrimaryNavigationButton.HIDDEN_LABEL.ALWAYS}
            key="right-button-1"
            icon={PrimaryNavigationButton.ICON.COMMENT}
            onClick={() => checkRoute("/notifications")}
            pill={
              unread > 0 ? (
                <Pill value={unread} variant={Pill.VARIANT.UNREAD} />
              ) : null
            }
          >
            <></>
          </PrimaryNavigationButton>,
          <PrimaryNavigationButton
            key="right-button-2"
            aria-label="Avatar"
            onClick={() => checkRoute("/profile")}
          >
            <Avatar icon={Avatar.ICON.USER} />
          </PrimaryNavigationButton>,
        ]}
        left={
          backButtonRoute || showBackToGeorgeButton ? [backButtonContent] : []
        }
        center={[userInfo]}
      />

      {logoutModalState && (
        <ModalComponent
          type="info"
          title={t("app.logoutConfirm.title")}
          text={t("app.logoutConfirm.message")}
          onClick={() => authContext.logoutHandler()}
          onClose={() => setLogoutModalState(false)}
          confirmationIcon={ICON.LOGOUT}
          cancelButtonText={t("common.no")}
          confirmButtonText={t("common.yes")}
        />
      )}

      {guard && (
        <ModalComponent
          type="info"
          title={t("common.error.title")}
          text={t("guard.select-mikro-company")}
          onClick={() => setGuard(false)}
          onClose={() => setGuard(false)}
          confirmationIcon={ICON.INFO}
          disableCancel={true}
          confirmButtonText={t("common.button.ok")}
        />
      )}

      {noMikro && (
        <ModalComponent
          type="info"
          title={t("common.error.title")}
          text={t("guard.no-mikro-company")}
          onClick={() => setNoMikro(false)}
          onClose={() => setNoMikro(false)}
          confirmationIcon={ICON.INFO}
          disableCancel={true}
          confirmButtonText={t("common.button.ok")}
        />
      )}
    </>
  )
}

export default Navbar
